import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  UtensilsCrossed, CalendarClock, ShoppingBag, Bike, Printer, Bell,
  CreditCard, TicketPercent, Globe, BarChart3, QrCode, Plug,
  ShieldAlert, Calculator, Receipt, MousePointerClick, Zap, Cake,
  ChevronLeft, ChevronRight, Sparkles, TrendingUp, Trophy, Mail,
  PiggyBank, Coins, FileBarChart, Maximize2,
} from "lucide-react";
import { api } from "@/lib/api";
import { isStandalone } from "@/lib/pwa";
import "./SalesPage.css";
import {
  PAIN_POINTS, SOLUTION_TILES, FEATURES, COMPARISON_HEADERS, COMPARISON_ROWS,
  CUISINES, TESTIMONIALS, FAQS, STEPS, HERO_CARDS,
} from "./salesContent";
import SalesLanguageBar from "./SalesLanguageBar";
import SalesTemplateGallery from "./SalesTemplateGallery";
import SalesItalianMenuDemo from "./SalesItalianMenuDemo";
import SalesNav from "./SalesNav";
import { LoyaltyVouchersBlock, CommunityBlock, ReviewsBlock, CrossRedeemBlock } from "./SalesExtraBlocks";
import HustlePlateLogo from "./HustlePlateLogo";

const FEATURE_ICONS = {
  UtensilsCrossed, CalendarClock, ShoppingBag, Bike, Printer, Bell,
  CreditCard, TicketPercent, Globe, BarChart3, QrCode, Plug,
  ShieldAlert, Calculator, Receipt, MousePointerClick, Zap, Cake,
};

// Runtime-configurable launch URLs. Set via window.__HP__ in index.html to
// override without a rebuild (Calendly link, lead-form endpoint, etc.).
const CFG = (typeof window !== "undefined" && window.__HP__) || {};
const CALENDAR_URL = CFG.CALENDAR_URL || "https://calendly.com/hustleplate/15min";
const DEMO_URL     = CFG.DEMO_URL     || "/?biz=demo-trattoria";
// Resolve image paths so they work BOTH in the main React app (served at /)
// AND in the standalone "drop-anywhere" Sales Page bundle that lives at
// /api/uploads/ssp/. CRA bakes process.env.PUBLIC_URL at build time, but we
// need the *runtime* hosting path so the bundle's /images stay reachable.
const IMG = (name) => {
  if (typeof window !== "undefined") {
    const p = window.location.pathname || "";
    const i = p.indexOf("/api/uploads/ssp");
    if (i !== -1) return `${p.slice(0, i)}/api/uploads/ssp/images/${name}`;
  }
  return `${process.env.PUBLIC_URL || ""}/images/${name}`;
};

/* ────────────────────────────────────────────────────────────────────── */
// TopNav is now the shared SalesNav (see ./SalesNav.jsx) so the same nav
// renders on /recipes and /recipes/:slug too, with all links resolving back
// to the salespage instead of leaking into the tenant restaurant.

/* ── Hero ─────────────────────────────────────────────────────────────── */
function Hero({ tx } = {}) {
  // tx (when present) is a translated overlay from /api/sales/i18n.
  const t = tx || {};
  return (
    <header className="sp-hero" data-testid="sales-hero">
      <div className="sp-container sp-hero__grid">
        <div>
          <div className="sp-hero__chip"><span /> {t.chip || "Aussie-built · Live in 1,000+ kitchens"}</div>
          <h1 className="sp-hero__title">
            {t.title_html ? (
              <span dangerouslySetInnerHTML={{ __html: t.title_html }} />
            ) : (
              <>Run your whole restaurant from <em>one tab.</em><br />(And keep the 30% the delivery apps were having.)</>
            )}
          </h1>
          <p className="sp-hero__sub">
            {t.sub || "Menu, bookings, online orders, table-side QR, delivery, payments, print PDFs — all in one platform. Starter plan is free forever, no commission, no per-cover fees, no fine print."}
          </p>
          <div className="sp-hero__ctas">
            <a href="#start" className="sp-btn sp-btn--primary" data-testid="sales-hero-cta">
              {t.cta_primary || "Get started — it's free"}
            </a>
            <a href={DEMO_URL} target="_blank" rel="noreferrer" className="sp-btn sp-btn--ghost" data-testid="sales-hero-demo">
              {t.cta_secondary || "See a live restaurant"}
            </a>
          </div>
          <div className="sp-hero__trust">
            <span>★★★★★ {t.trust_rating || "4.9/5 from 380+ Aussie owners"}</span>
            <span>· {t.trust_card || "No credit card to start"}</span>
            <span>· {t.trust_setup || "Setup in under an hour"}</span>
          </div>
        </div>
        <div className="sp-hero__art" aria-hidden="true">
          <div className="sp-hero__photo">
            <img src={IMG("hero-dashboard.jpg")} alt="" loading="eager" />
            <div className="sp-hero__photo-overlay" />
          </div>
          {HERO_CARDS.map((c, i) => (
            <div key={i} className={`sp-hero__card sp-hero__card--${i + 1}`}>
              <div className="sp-hero__card-eyebrow">{c.eyebrow}</div>
              <div className="sp-hero__card-headline">{c.h}</div>
              <div className="sp-hero__card-body">{c.b}</div>
            </div>
          ))}
        </div>
      </div>
    </header>
  );
}

/* ── Payment logo strip ──────────────────────────────────────────────── */
function PaymentStrip() {
  return (
    <section className="sp-strip" data-testid="sales-payments">
      <div className="sp-container sp-strip__inner">
        <div className="sp-strip__label">Take payment any way they want</div>
        <div className="sp-strip__logos">
          <span className="sp-pay sp-pay--stripe">Stripe</span>
          <span className="sp-pay sp-pay--square">Square</span>
          <span className="sp-pay sp-pay--visa">VISA</span>
          <span className="sp-pay sp-pay--mc">Mastercard</span>
          <span className="sp-pay sp-pay--amex">AMEX</span>
          <span className="sp-pay sp-pay--apple"></span>
          <span className="sp-pay sp-pay--google">G Pay</span>
          <span className="sp-pay sp-pay--afterpay">Afterpay</span>
          <span className="sp-pay sp-pay--zip">Zip</span>
          <span className="sp-pay sp-pay--paypal">PayPal</span>
        </div>
      </div>
    </section>
  );
}

/* ── Pain points ─────────────────────────────────────────────────────── */
function PainPoints() {
  return (
    <section className="sp-section sp-section--paper" data-testid="sales-pain">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">Sound familiar?</div>
            <h2 className="sp-h">Running a restaurant in 2026 is bloody hard.</h2>
            <p className="sp-lead">
              We talked to 400 Aussie owners. Same six headaches came up every
              single time. We built this so you stop having them.
            </p>
          </div>
        </div>
        <div className="sp-pain__grid">
          {PAIN_POINTS.map((p, i) => (
            <article key={i} className="sp-pain__card" data-testid={`sales-pain-${i}`}>
              <div className="sp-pain__icon" aria-hidden="true">{p.icon}</div>
              <h3 className="sp-pain__h">{p.title}</h3>
              <p className="sp-pain__p">{p.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Solution split ──────────────────────────────────────────────────── */
function SolutionSplit() {
  return (
    <section className="sp-section sp-section--cream" data-testid="sales-solution">
      <div className="sp-container sp-solution__grid">
        <div>
          <div className="sp-eyebrow">Our take</div>
          <h2 className="sp-h">One platform. One bill. One log-in.<br />Cut the cable spaghetti.</h2>
          <p className="sp-lead">
            Most venues are held together with six different subscriptions that
            don't talk to each other — and a spreadsheet nobody trusts. We
            rebuilt the lot as one calm, beautifully designed system you can
            run from the pass on your phone.
          </p>
          <ul className="sp-solution__points">
            <li>Bin OpenTable, Mr Yum, Square Online <em>and</em> the menu printer — one log-in does the lot.</li>
            <li>Built for hospo by people who've actually run a Friday night service.</li>
            <li><strong>New:</strong> Coffee pre-order with add-ons, dietary chips and a live cart that follows the guest across every page.</li>
            <li><strong>New:</strong> Recipe &amp; Margin engine — punch in ingredients, see live COGS and profit margin on every menu item.</li>
            <li><strong>New:</strong> 2,400-recipe Salespage Portal with one-tap share cards for Instagram, Pinterest and WhatsApp — free traffic, on tap.</li>
            <li>Honest pricing. Free to start. Cancel any time and walk out with your data.</li>
            <li>Aussie support, Aussie data centres, Aussie owned. Fair dinkum.</li>
          </ul>
          {!isStandalone() && (
            <a href="#pricing" className="sp-btn sp-btn--dark" data-testid="sales-solution-cta">See the pricing</a>
          )}
        </div>
        <div className="sp-solution__art" aria-hidden="true">
          {SOLUTION_TILES.map((t) => (
            <div key={t.n} className="sp-solution__tile">
              <div className="sp-solution__tile-num">{t.n}</div>
              <div className="sp-solution__tile-h">{t.h}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Feature grid ────────────────────────────────────────────────────── */
function FeatureGrid() {
  return (
    <section id="features" className="sp-section sp-section--paper" data-testid="sales-features">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">What's in the box</div>
            <h2 className="sp-h">Eighteen things you probably pay for already.<br />Now they live in one place.</h2>
          </div>
        </div>
        <div className="sp-features__grid">
          {FEATURES.map((f) => {
            const Icon = FEATURE_ICONS[f.icon];
            return (
              <article key={f.h} className="sp-feature" data-testid={`sales-feature-${f.h.toLowerCase().split(' ').slice(0,2).join('-')}`}>
                <div className="sp-feature__icon">{Icon ? <Icon strokeWidth={1.5} /> : null}</div>
                <h3 className="sp-feature__h">{f.h}</h3>
                <p className="sp-feature__p">{f.p}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Comparison table ────────────────────────────────────────────────── */
function CompareTable() {
  function cell(v, key) {
    if (v === "✓")     return <span className="sp-compare__tick">✓</span>;
    if (v === "—")     return <span className="sp-compare__cross">—</span>;
    if (v.startsWith("us-")) {
      const inner = v.replace("us-", "");
      return <td key={key} className="sp-compare__us-cell"><span className="sp-compare__tick">{inner === "✓" ? "✓" : inner}</span></td>;
    }
    return v;
  }
  return (
    <section id="compare" className="sp-section sp-section--cream" data-testid="sales-compare">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">How we stack up</div>
            <h2 className="sp-h">Not all platforms are equal.<br />Some are even rude.</h2>
            <p className="sp-lead">
              We did the homework so you don't have to. Numbers checked against
              public pricing pages, Feb 2026.
            </p>
          </div>
        </div>
        <div className="sp-compare">
          <table className="sp-compare__table">
            <thead>
              <tr>
                {COMPARISON_HEADERS.map((h, i) => (
                  <th key={i} className={i === COMPARISON_HEADERS.length - 1 ? "sp-compare__us" : ""}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr key={i} data-testid={`sales-compare-row-${i}`}>
                  {row.map((v, j) => {
                    if (typeof v === "string" && v.startsWith("us-")) {
                      const inner = v.replace("us-", "");
                      return (
                        <td key={j} className="sp-compare__us-cell">
                          {inner === "✓" ? <span className="sp-compare__tick">✓</span> : inner}
                        </td>
                      );
                    }
                    return <td key={j}>{cell(v, j)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ── Cuisine showcase ─────────────────────────────────────────────────── */
function CuisineShowcase() {
  return (
    <section className="sp-section sp-section--paper" data-testid="sales-cuisines">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">70+ templates · 30+ cuisines</div>
            <h2 className="sp-h">Doesn't matter what you cook.<br />There's a template that already gets it.</h2>
            <p className="sp-lead">
              Pick a starting point, swap your colours, drop in your photos.
              Or chuck it out and use ours — they're licensed, royalty-free.
            </p>
          </div>
          <a href={DEMO_URL} target="_blank" rel="noreferrer" className="sp-btn sp-btn--ghost" data-testid="sales-cuisine-demo">
            Browse the live demo →
          </a>
        </div>
        <div className="sp-cuisines__grid">
          {CUISINES.map((c) => (
            <a key={c.k} href={c.demo} target="_blank" rel="noreferrer" className={`sp-cuisine sp-cuisine--${c.k}`} data-testid={`sales-cuisine-${c.k}`}>
              <div>
                <div className="sp-cuisine__c">{c.c}</div>
                <div className="sp-cuisine__t">{c.t}</div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Pricing ──────────────────────────────────────────────────────────── */
function Pricing({ packs }) {
  // Apple 3.1.3(b): never render pricing / purchase CTAs inside the wrapper
  if (isStandalone()) return null;
  return (
    <section id="pricing" className="sp-section sp-section--cream" data-testid="sales-pricing">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">Honest pricing</div>
            <h2 className="sp-h">Free to start.<br />No commission. No catch.</h2>
            <p className="sp-lead">
              All plans include unlimited menu items, bookings, customers and
              orders. You only pay more when you want more features —
              never per cover, never per order.
            </p>
          </div>
        </div>
        <div className="sp-pricing__grid">
          {packs.map((p, i) => (
            <article
              key={p.key}
              className={`sp-tier ${p.key === "pro" ? "sp-tier--featured" : ""}`}
              data-testid={`sales-tier-${p.key}`}
            >
              {p.key === "pro" && <span className="sp-tier__badge">Most popular</span>}
              <h3 className="sp-tier__name">{p.label}</h3>
              <p className="sp-tier__tagline">{p.tagline}</p>
              <div className="sp-tier__price">
                {p.price_aud_monthly === 0 && "Free"}
                {p.price_aud_monthly > 0 && (<>${p.price_aud_monthly}<small>/mo AUD</small></>)}
                {p.price_aud_monthly === null && "Quote"}
              </div>
              <ul className="sp-tier__list">
                <li>{p.modules?.length || 0} modules unlocked</li>
                {p.key === "starter"   && <><li>One-click templates</li><li>Stripe payments</li><li>Bookings + menu</li><li>Customer accounts</li></>}
                {p.key === "standard"  && <><li>CSV menu import + AI mapping</li><li>Holiday menus + specials</li><li>Email marketing (Resend)</li><li>Reviews + multi-language</li></>}
                {p.key === "pro"       && <><li>Table-side QR ordering</li><li>Delivery (Uber Direct etc.)</li><li>SMS + push + loyalty</li><li>Print-ready PDF menus</li></>}
                {p.key === "enterprise"&& <><li>Multi-location chains</li><li>In-house driver app</li><li>White-label + custom domains</li><li>Priority Aussie phone support</li></>}
              </ul>
              <a href="#start" className="sp-btn sp-btn--primary sp-tier__cta" data-testid={`sales-tier-${p.key}-cta`}>
                {p.price_aud_monthly === 0 ? "Get started free" : p.price_aud_monthly === null ? "Talk to sales" : "Start free trial"}
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Testimonials ─────────────────────────────────────────────────────── */
function Testimonials() {
  return (
    <section className="sp-section sp-section--paper" data-testid="sales-testimonials">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">Real owners, real opinions</div>
            <h2 className="sp-h">Restaurants don't gush.<br />But these mob did.</h2>
            <p className="sp-lead">
              Six 5-star verbatims, sent straight from owners' phones after their first month live. Every name links back to a real Aussie venue.
            </p>
          </div>
        </div>
        <div className="sp-tests__grid">
          {TESTIMONIALS.map((t, i) => (
            <article key={i} className="sp-test" data-testid={`sales-test-${i}`}>
              <div className="sp-test__stars">★★★★★</div>
              <p className="sp-test__quote">"{t.quote}"</p>
              <div className="sp-test__row">
                <div className="sp-test__logo">{t.logo}</div>
                <div>
                  <div className="sp-test__name">{t.name}</div>
                  <div className="sp-test__role">{t.role}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── ROI calculator ───────────────────────────────────────────────────── */
function RoiCalculator() {
  const [covers, setCovers] = useState(80);
  const [avgSpend, setAvgSpend] = useState(45);
  const [days, setDays] = useState(6);
  const annualRecovered = useMemo(() => {
    // Assumed: ~12% of revenue currently lost to delivery commissions + booking fees + missed reorders.
    const weekly = covers * avgSpend * days;
    const yearly = weekly * 52;
    return Math.round(yearly * 0.12);
  }, [covers, avgSpend, days]);
  return (
    <section className="sp-section sp-section--paper" data-testid="sales-roi">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">The big number</div>
            <h2 className="sp-h">Estimate what you're leaving on the table.</h2>
            <p className="sp-lead">
              Most kitchens are giving away 10-15% of their top line to delivery
              fees, no-show bookings and discounted vouchers. Tweak the dials.
            </p>
          </div>
        </div>
        <div className="sp-roi">
          <div className="sp-roi__inputs">
            <div className="sp-roi__field">
              <label htmlFor="roi-covers">Average covers per service</label>
              <input id="roi-covers" type="number" min="10" max="500" value={covers} onChange={(e) => setCovers(parseInt(e.target.value || "0", 10))} data-testid="roi-covers" />
            </div>
            <div className="sp-roi__field">
              <label htmlFor="roi-spend">Average spend per cover (AUD)</label>
              <input id="roi-spend" type="number" min="5" max="500" value={avgSpend} onChange={(e) => setAvgSpend(parseInt(e.target.value || "0", 10))} data-testid="roi-spend" />
            </div>
            <div className="sp-roi__field">
              <label htmlFor="roi-days">Trading days per week</label>
              <input id="roi-days" type="number" min="1" max="7" value={days} onChange={(e) => setDays(parseInt(e.target.value || "0", 10))} data-testid="roi-days" />
            </div>
          </div>
          <div className="sp-roi__result">
            <div className="sp-roi__result-num" data-testid="roi-result">
              ${annualRecovered.toLocaleString()}
            </div>
            <div className="sp-roi__result-l">recovered per year</div>
            <small>Estimate based on a 12% margin recapture across delivery, booking & marketing fees. Your numbers will vary.</small>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── HustlePlate Savings Calculator (lead-gated, multi-tier comparison) ── */
function HustleSavingsCalculator() {
  const [pricing, setPricing] = useState(null);
  const [costs, setCosts] = useState({});
  const [postcodeSuburbs, setPostcodeSuburbs] = useState([]);
  const [qual, setQual] = useState({
    missed_calls_per_month: "",
    no_shows_per_month: "",
    menus_unreadable: null,        // null | true | false (tri-state for Y/N)
    interest_vouchers: null,
    interest_rewards: null,
    interest_vip_club: null,
    interest_birthday_club: null,
    interest_coop: null,
    interest_push_app: null,
  });
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    mobile: "", business_name: "", postcode: "",
  });
  // step: 0 = lead-gate form, 1 = cost inputs revealed, 2 = comparison result
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [fastTrackCountdown, setFastTrackCountdown] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    fetch(`${API}/api/directory/pricing`).then(r => r.json()).then(setPricing).catch(() => {});
  }, []);

  // Postcode → suburb resolver (debounced)
  useEffect(() => {
    const pc = form.postcode.trim();
    if (!/^\d{4}$/.test(pc)) { setPostcodeSuburbs([]); return; }
    const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    const t = setTimeout(() => {
      fetch(`${API}/api/directory/locations/postcode/${pc}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => setPostcodeSuburbs(d?.suburbs || []))
        .catch(() => setPostcodeSuburbs([]));
    }, 220);
    return () => clearTimeout(t);
  }, [form.postcode]);

  const totalCurrent = useMemo(
    () => Object.values(costs).reduce((s, v) => s + (Number(v) || 0), 0),
    [costs],
  );

  const validForm = () =>
    form.first_name.trim() &&
    form.last_name.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.mobile.trim() &&
    form.business_name.trim() &&
    /^\d{4}$/.test(form.postcode.trim());

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
      // We always submit with tier=pro so the headline saving is on Premium;
      // the response always returns the full Starter vs Pro comparison.
      const r = await fetch(`${API}/api/directory/savings-calculator/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          current_costs: costs,
          qualitative: {
            missed_calls_per_month: Number(qual.missed_calls_per_month) || 0,
            no_shows_per_month:     Number(qual.no_shows_per_month) || 0,
            menus_unreadable:       qual.menus_unreadable === true,
            interest_vouchers:      qual.interest_vouchers === true,
            interest_rewards:       qual.interest_rewards === true,
            interest_vip_club:      qual.interest_vip_club === true,
            interest_birthday_club: qual.interest_birthday_club === true,
            interest_coop:          qual.interest_coop === true,
            interest_push_app:      qual.interest_push_app === true,
          },
          tier: "pro",
        }),
      });
      const data = await r.json();
      if (!r.ok || data.ok === false) {
        setError(data.detail || data.error || "Something went wrong. Please try again.");
        return;
      }
      setResult(data);
      setStep(2);
      // High-value lead → fast-track Claim & Onboard auto-redirect
      if (data.is_high_value_lead) {
        setFastTrackCountdown(15);
      }
    } catch (e) {
      setError("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Tick the fast-track redirect countdown
  useEffect(() => {
    if (fastTrackCountdown === null) return;
    if (fastTrackCountdown <= 0) {
      const qs = new URLSearchParams({
        biz: form.business_name,
        first: form.first_name, last: form.last_name,
        email: form.email, mobile: form.mobile, postcode: form.postcode,
        from: "savings-calc", saving: String(result?.comparison?.platinum?.monthly_saving ?? ""),
      }).toString();
      window.location.href = `/admin/claim-onboard?${qs}`;
      return;
    }
    const t = setTimeout(() => setFastTrackCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [fastTrackCountdown, form, result]);

  return (
    <section id="savings-calculator" className="sp-section sp-section--cream" data-testid="sales-savings-calculator">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">Your custom report</div>
            <h2 className="sp-h">How much would HustlePlate save your restaurant?</h2>
            <p className="sp-lead">
              Tell us a bit about your restaurant and we'll unlock a custom
              calculator — fill it out and <strong>we'll report back what you can save</strong>.
            </p>
          </div>
        </div>

        {!pricing && (
          <div className="hsc-loading" data-testid="hsc-loading">Loading current market rates…</div>
        )}

        {/* Step 0 — Lead capture (GATE before the calculator) */}
        {pricing && step === 0 && (
          <div className="hsc-card" data-testid="hsc-step-lead">
            <div className="hsc-gate-banner">
              <span className="hsc-gate-banner__pill">Free · 60-second savings report</span>
              <h3 className="hsc-h hsc-h--gate">Where should we send your report?</h3>
              <p className="hsc-sub">
                Enter your details to unlock the calculator. We'll show your
                savings on-screen <strong>and</strong> email you a branded
                report.
              </p>
            </div>
            <div className="hsc-form">
              <div className="hsc-form__row">
                <Field label="First name" v={form.first_name} on={(v) => setForm({ ...form, first_name: v })} testid="hsc-first-name" />
                <Field label="Last name"  v={form.last_name}  on={(v) => setForm({ ...form, last_name: v })}  testid="hsc-last-name" />
              </div>
              <Field label="Email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="hsc-email" />
              <div className="hsc-form__row">
                <Field label="Mobile number" type="tel" v={form.mobile} on={(v) => setForm({ ...form, mobile: v })} testid="hsc-mobile" />
                <div className="hsc-field">
                  <label className="hsc-field__label">Postcode</label>
                  <input
                    className="hsc-field__input"
                    type="text"
                    value={form.postcode}
                    onChange={(e) => setForm({ ...form, postcode: e.target.value.replace(/\D/g,"").slice(0,4) })}
                    data-testid="hsc-postcode"
                  />
                  {postcodeSuburbs.length > 0 && (
                    <div className="hsc-postcode-resolved" data-testid="hsc-postcode-resolved">
                      <span className="hsc-postcode-resolved__pip" /> {postcodeSuburbs[0].suburb_name} · {postcodeSuburbs[0].state_code}
                      {postcodeSuburbs.length > 1 && <em> +{postcodeSuburbs.length - 1} more</em>}
                    </div>
                  )}
                </div>
              </div>
              <Field label="Restaurant / business name" v={form.business_name} on={(v) => setForm({ ...form, business_name: v })} testid="hsc-business-name" />
            </div>
            <div className="hsc-bar">
              <button
                type="button"
                disabled={!validForm()}
                onClick={() => setStep(1)}
                data-testid="hsc-unlock"
                className="sp-btn sp-btn--primary"
              >
                Unlock my calculator →
              </button>
            </div>
            <p className="hsc-fine">By submitting you agree to receive your savings report and a brief follow-up from the HustlePlate team. No spam, ever.</p>
          </div>
        )}

        {/* Step 1 — Cost inputs (revealed AFTER lead capture, NO tier picker) */}
        {pricing && step === 1 && (
          <div className="hsc-card" data-testid="hsc-step-costs">
            <div className="hsc-greeting" data-testid="hsc-greeting">
              G'day <strong>{form.first_name}</strong> — what does <strong>{form.business_name}</strong> currently pay?
            </div>
            <p className="hsc-sub">Enter the monthly $ for each service. Leave blank or 0 if you don't pay for it. We'll generate a full comparison on the next screen.</p>
            <div className="hsc-rows">
              {pricing.replaces.map((r) => (
                <div className="hsc-row" key={r.slug} data-testid={`hsc-cost-row-${r.slug}`}>
                  <div className="hsc-row__label">
                    <div className="hsc-row__name">{r.label}</div>
                    <div className="hsc-row__hint">Typical market: ${r.low}–${r.high}/mo</div>
                  </div>
                  <div className="hsc-row__input">
                    <span className="hsc-row__currency">$</span>
                    <input
                      type="number" inputMode="numeric" min="0"
                      value={costs[r.slug] ?? ""}
                      onChange={(e) => setCosts({ ...costs, [r.slug]: e.target.value })}
                      placeholder="0"
                      data-testid={`hsc-cost-input-${r.slug}`}
                    />
                    <span className="hsc-row__suffix">/mo</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pain-point numerics: missed calls + no-shows */}
            <div className="hsc-subhead">Lost revenue we can help you recover</div>
            <div className="hsc-rows">
              <div className="hsc-row" data-testid="hsc-qual-missed-calls">
                <div className="hsc-row__label">
                  <div className="hsc-row__name">Missed reservation calls per month</div>
                  <div className="hsc-row__hint">Calls that went unanswered. We estimate ≈ $75 lost per missed booking call.</div>
                </div>
                <div className="hsc-row__input">
                  <input
                    type="number" inputMode="numeric" min="0"
                    value={qual.missed_calls_per_month}
                    onChange={(e) => setQual({ ...qual, missed_calls_per_month: e.target.value })}
                    placeholder="0"
                    data-testid="hsc-qual-missed-calls-input"
                  />
                  <span className="hsc-row__suffix">/mo</span>
                </div>
              </div>
              <div className="hsc-row" data-testid="hsc-qual-no-shows">
                <div className="hsc-row__label">
                  <div className="hsc-row__name">No-show reservations per month</div>
                  <div className="hsc-row__hint">Bookings that never showed. SMS reminders typically save ≈ $120 per recovered booking.</div>
                </div>
                <div className="hsc-row__input">
                  <input
                    type="number" inputMode="numeric" min="0"
                    value={qual.no_shows_per_month}
                    onChange={(e) => setQual({ ...qual, no_shows_per_month: e.target.value })}
                    placeholder="0"
                    data-testid="hsc-qual-no-shows-input"
                  />
                  <span className="hsc-row__suffix">/mo</span>
                </div>
              </div>
            </div>

            {/* Y/N pain */}
            <div className="hsc-subhead">A quick yes/no</div>
            <YnRow
              testid="hsc-qual-menus-unreadable"
              label="Customers can't read your menus (small text, dark photos, lost printouts)?"
              v={qual.menus_unreadable}
              on={(v) => setQual({ ...qual, menus_unreadable: v })}
            />

            {/* Y/N interests — drive HustleCRM tagging for follow-up segments */}
            <div className="hsc-subhead">Would you be interested in…</div>
            <div className="hsc-yn-grid">
              <YnRow testid="hsc-int-vouchers"      label="Running your own vouchers & coupons"
                     v={qual.interest_vouchers}      on={(v) => setQual({ ...qual, interest_vouchers: v })} />
              <YnRow testid="hsc-int-rewards"       label="Your own customer rewards system"
                     v={qual.interest_rewards}       on={(v) => setQual({ ...qual, interest_rewards: v })} />
              <YnRow testid="hsc-int-vip"           label="Your own VIP Club"
                     v={qual.interest_vip_club}      on={(v) => setQual({ ...qual, interest_vip_club: v })} />
              <YnRow testid="hsc-int-birthday"      label="Your own Birthday Club"
                     v={qual.interest_birthday_club} on={(v) => setQual({ ...qual, interest_birthday_club: v })} />
              <YnRow testid="hsc-int-coop"          label="Joining a local business co-operative (businesses helping businesses)"
                     v={qual.interest_coop}          on={(v) => setQual({ ...qual, interest_coop: v })} />
              <YnRow testid="hsc-int-push"          label="Your own branded phone app to push slow-day offers to regulars"
                     v={qual.interest_push_app}      on={(v) => setQual({ ...qual, interest_push_app: v })} />
            </div>
            {error && <div className="hsc-error" data-testid="hsc-error">{error}</div>}
            <div className="hsc-bar">
              <div className="hsc-bar__total">Running total: <strong>${totalCurrent.toFixed(0)}/mo</strong></div>
              <button type="button" onClick={() => setStep(0)} className="sp-btn sp-btn--ghost" data-testid="hsc-edit-details">Edit details</button>
              <button
                type="button"
                disabled={totalCurrent === 0 || busy}
                onClick={submit}
                data-testid="hsc-submit"
                className="sp-btn sp-btn--primary"
              >
                {busy ? "Calculating…" : "Show me my savings"}
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Result with full Starter vs Pro comparison */}
        {step === 2 && result && (
          <div className="hsc-card hsc-card--result" data-testid="hsc-step-result">
            {/* High-value fast-track banner */}
            {result.is_high_value_lead && fastTrackCountdown !== null && (
              <div className="hsc-fasttrack" data-testid="hsc-fasttrack">
                <div className="hsc-fasttrack__pill">High-value match · Fast-track unlocked</div>
                <div className="hsc-fasttrack__msg">
                  You'd save <strong>${Math.abs(result.comparison?.platinum?.monthly_saving || 0).toLocaleString()}/mo</strong>.
                  Redirecting to your pre-filled <strong>"Watch It Live"</strong> Claim & Onboard in <strong>{fastTrackCountdown}s</strong>…
                </div>
                <div className="hsc-fasttrack__btns">
                  <button onClick={() => setFastTrackCountdown(0)} className="sp-btn sp-btn--primary" data-testid="hsc-fasttrack-go">Go now →</button>
                  <button onClick={() => setFastTrackCountdown(null)} className="sp-btn sp-btn--ghost" data-testid="hsc-fasttrack-cancel">Stay on this page</button>
                </div>
              </div>
            )}

            <ComparisonResult result={result} form={form} />

            <div className="hsc-bar">
              <button type="button" onClick={() => { setStep(1); setResult(null); setFastTrackCountdown(null); }} className="sp-btn sp-btn--ghost" data-testid="hsc-restart">
                Edit my costs
              </button>
              <Link to="/add-business" className="sp-btn sp-btn--primary" data-testid="hsc-cta-add-business">
                Get started — Add my business
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Result panel: PowerPoint-style slide deck ─────────────────────────── */
function ComparisonResult({ result, form }) {
  const cmp  = result.comparison || {};
  const std  = cmp.standard || {};
  const plat = cmp.platinum || {};

  // ── Maths ────────────────────────────────────────────────────────────
  const totalCurrent = Number(result.total_current_monthly || 0);
  const platCost     = Number(plat.monthly_price || 0);
  const stdCost      = Number(std.monthly_price || 0);

  // A = Original Savings (replace existing tools w/ HustlePlate Pro)
  const savingsMo  = Math.max(totalCurrent - platCost, 0);
  const savingsYr  = savingsMo * 12;

  // B = Added Income — new revenue captured by activating the qualitative
  // services (kind: "opportunity" = brand-new streams, "pain" = recaptured
  // lost revenue). "replaces" rows are already represented in A so excluded.
  const mot = result.money_on_table || [];
  const addedIncomeMo = mot
    .filter((m) => m.kind === "opportunity" || m.kind === "pain")
    .reduce((s, m) => s + Number(m.uplift_mo || 0), 0);
  const addedIncomeYr = addedIncomeMo * 12;

  // Finale = A + B
  const totalGainMo = savingsMo + addedIncomeMo;
  const totalGainYr = totalGainMo * 12;

  const incomeRows = mot.filter((m) => m.kind === "opportunity" || m.kind === "pain");

  // ── Slide deck navigation ───────────────────────────────────────────
  const slides = [
    { id: "cover",   title: "Cover",                          icon: FileBarChart },
    { id: "costs",   title: "Your current monthly costs",     icon: Receipt },
    { id: "compare", title: "Starter vs Pro",                icon: Calculator },
    { id: "savings", title: "A · Original Savings",           icon: PiggyBank },
    { id: "income",  title: "B · Added Income",               icon: Coins },
    { id: "finale",  title: "A + B · Total Revenue Gain",     icon: Trophy },
    { id: "next",    title: "What's next",                    icon: Mail },
  ];
  const [slide, setSlide] = useState(0);
  const total = slides.length;
  const goPrev = () => setSlide((s) => Math.max(0, s - 1));
  const goNext = () => setSlide((s) => Math.min(total - 1, s + 1));

  // Keyboard arrow nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const Active = slides[slide].icon;

  return (
    <div className="hsc-deck" data-testid="hsc-deck">
      {/* ── Deck chrome (slide counter, presentation title, dots) ── */}
      <div className="hsc-deck__chrome">
        <div className="hsc-deck__chrome-l">
          <div className="hsc-deck__brand">HustlePlate · Savings Report</div>
          <div className="hsc-deck__biz" data-testid="hsc-deck-biz">{form.business_name}</div>
        </div>
        <div className="hsc-deck__chrome-r">
          <div className="hsc-deck__counter" data-testid="hsc-deck-counter">
            <strong>{slide + 1}</strong><span>/ {total}</span>
          </div>
        </div>
      </div>

      {/* ── Slide stage ─────────────────────────────────────────────── */}
      <div className={`hsc-slide hsc-slide--${slides[slide].id}`} data-testid={`hsc-slide-${slides[slide].id}`}>
        {/* ── Slide 1: Cover ── */}
        {slide === 0 && (
          <div className="hsc-slide__cover">
            <Active className="hsc-slide__cover-mark" />
            <div className="hsc-slide__eyebrow">Savings Report · prepared for</div>
            <h2 className="hsc-slide__cover-h">{form.business_name}</h2>
            <div className="hsc-slide__cover-meta">
              <span>{form.first_name} {form.last_name}</span>
              <span>·</span>
              <span>{form.email}</span>
              <span>·</span>
              <span>{form.postcode}</span>
            </div>
            <p className="hsc-slide__cover-lead">
              A 6-slide walk-through of where your money goes today, what
              HustlePlate replaces, and the new revenue we unlock. Use the
              arrows below — or your keyboard's <kbd>←</kbd> / <kbd>→</kbd> — to step through.
            </p>
            <div className="hsc-slide__cover-pillars">
              <div className="hsc-slide__cover-pillar"><PiggyBank /><span>A · Savings</span></div>
              <div className="hsc-slide__cover-pillar hsc-slide__cover-pillar--plus">+</div>
              <div className="hsc-slide__cover-pillar"><Coins /><span>B · Added income</span></div>
              <div className="hsc-slide__cover-pillar hsc-slide__cover-pillar--eq">=</div>
              <div className="hsc-slide__cover-pillar hsc-slide__cover-pillar--final"><Trophy /><span>Total gain</span></div>
            </div>
          </div>
        )}

        {/* ── Slide 2: Current costs ── */}
        {slide === 1 && (
          <div className="hsc-slide__body">
            <div className="hsc-slide__head">
              <div className="hsc-slide__eyebrow">Slide 2 · the picture today</div>
              <h3 className="hsc-slide__h">What you're paying every month, right now</h3>
              <p className="hsc-slide__sub">
                We totalled the line items you entered. Every one of these is
                replaced — or made dramatically cheaper — by HustlePlate.
              </p>
            </div>
            <div className="hsc-slide__costs">
              {(result.line_items || []).map((li) => (
                <div className="hsc-slide__cost-row" key={li.slug} data-testid={`hsc-slide-cost-${li.slug}`}>
                  <span className="hsc-slide__cost-label">{li.label}</span>
                  <span className="hsc-slide__cost-num">${Number(li.monthly).toFixed(0)}<small>/mo</small></span>
                </div>
              ))}
              <div className="hsc-slide__cost-row hsc-slide__cost-row--total" data-testid="hsc-slide-cost-total">
                <span className="hsc-slide__cost-label"><strong>Total monthly outlay</strong></span>
                <span className="hsc-slide__cost-num hsc-slide__cost-num--red">${totalCurrent.toLocaleString()}<small>/mo</small></span>
              </div>
            </div>
            <p className="hsc-slide__footnote">
              That's <strong>${(totalCurrent * 12).toLocaleString()}/year</strong> flowing out to a stack of separate vendors.
            </p>
          </div>
        )}

        {/* ── Slide 3: Starter vs Pro comparison ── */}
        {slide === 2 && (
          <div className="hsc-slide__body">
            <div className="hsc-slide__head">
              <div className="hsc-slide__eyebrow">Slide 3 · the replacement</div>
              <h3 className="hsc-slide__h">One platform · two tiers · everything you need</h3>
              <p className="hsc-slide__sub">
                Each row below is something you're currently paying for elsewhere. Both HustlePlate tiers include all of it.
              </p>
            </div>
            <div className="hsc-cmp-table" data-testid="hsc-cmp-table">
              <div className="hsc-cmp-table__head">
                <div className="hsc-cmp-cell hsc-cmp-cell--label">Line item</div>
                <div className="hsc-cmp-cell hsc-cmp-cell--current">Your current</div>
                <div className="hsc-cmp-cell hsc-cmp-cell--std">Starter</div>
                <div className="hsc-cmp-cell hsc-cmp-cell--plat">Pro</div>
              </div>
              {(result.line_items || []).map((li) => (
                <div className="hsc-cmp-table__row" key={li.slug} data-testid={`hsc-cmp-line-${li.slug}`}>
                  <div className="hsc-cmp-cell hsc-cmp-cell--label">{li.label}</div>
                  <div className="hsc-cmp-cell hsc-cmp-cell--current">${Number(li.monthly).toFixed(0)}/mo</div>
                  <div className="hsc-cmp-cell hsc-cmp-cell--std hsc-cmp-cell--check">included</div>
                  <div className="hsc-cmp-cell hsc-cmp-cell--plat hsc-cmp-cell--check">included</div>
                </div>
              ))}
              {(cmp.premium_extra_features || []).map((f, i) => (
                <div className="hsc-cmp-table__row hsc-cmp-table__row--premium" key={`pf-${i}`} data-testid={`hsc-cmp-premium-${i}`}>
                  <div className="hsc-cmp-cell hsc-cmp-cell--label">+ {f}</div>
                  <div className="hsc-cmp-cell hsc-cmp-cell--current">—</div>
                  <div className="hsc-cmp-cell hsc-cmp-cell--std hsc-cmp-cell--out">—</div>
                  <div className="hsc-cmp-cell hsc-cmp-cell--plat hsc-cmp-cell--check">included</div>
                </div>
              ))}
              <div className="hsc-cmp-table__row hsc-cmp-table__row--totals" data-testid="hsc-cmp-totals">
                <div className="hsc-cmp-cell hsc-cmp-cell--label">Monthly total</div>
                <div className="hsc-cmp-cell hsc-cmp-cell--current"><strong>${totalCurrent.toLocaleString()}/mo</strong></div>
                <div className="hsc-cmp-cell hsc-cmp-cell--std"><strong>${stdCost}/mo</strong></div>
                <div className="hsc-cmp-cell hsc-cmp-cell--plat"><strong>${platCost}/mo</strong></div>
              </div>
            </div>
          </div>
        )}

        {/* ── Slide 4: A — Original Savings ── */}
        {slide === 3 && (
          <div className="hsc-slide__body hsc-slide__body--green">
            <div className="hsc-slide__head">
              <div className="hsc-slide__eyebrow hsc-slide__eyebrow--green">Slide 4 · pillar A</div>
              <h3 className="hsc-slide__h">Original Savings — what you stop paying</h3>
              <p className="hsc-slide__sub">
                Replace your stack with HustlePlate {plat.tier_name || "Pro"}. The money you currently send
                to multiple vendors stays in your business.
              </p>
            </div>
            <div className="hsc-slide__bignum hsc-slide__bignum--green" data-testid="hsc-slide-savings">
              <div className="hsc-slide__bignum-eyebrow">A · Original Savings</div>
              <div className="hsc-slide__bignum-num">${savingsMo.toLocaleString()}<small>/mo</small></div>
              <div className="hsc-slide__bignum-sub">≈ <strong>${savingsYr.toLocaleString()}</strong> per year</div>
            </div>
            <div className="hsc-slide__formula">
              <div className="hsc-slide__formula-row">
                <span>${totalCurrent.toLocaleString()}/mo</span>
                <span className="hsc-slide__formula-op">−</span>
                <span>${platCost.toLocaleString()}/mo</span>
                <span className="hsc-slide__formula-op">=</span>
                <strong>${savingsMo.toLocaleString()}/mo</strong>
              </div>
              <p className="hsc-slide__formula-note">Current monthly outlay minus HustlePlate {plat.tier_name || "Pro"} subscription.</p>
            </div>
          </div>
        )}

        {/* ── Slide 5: B — Added Income ── */}
        {slide === 4 && (
          <div className="hsc-slide__body hsc-slide__body--amber">
            <div className="hsc-slide__head">
              <div className="hsc-slide__eyebrow hsc-slide__eyebrow--amber">Slide 5 · pillar B</div>
              <h3 className="hsc-slide__h">Added Income — new revenue we unlock</h3>
              <p className="hsc-slide__sub">
                Each card is a stream you flagged interest in (or a leak we plug). Numbers are AU café/restaurant
                benchmarks — we'll show you our maths.
              </p>
            </div>
            {incomeRows.length > 0 ? (
              <div className="hsc-slide__income-grid" data-testid="hsc-slide-income-grid">
                {incomeRows.map((m) => (
                  <div key={m.key} className={`hsc-slide__income-card hsc-slide__income-card--${m.kind}`} data-testid={`hsc-slide-income-${m.key}`}>
                    <div className="hsc-slide__income-kind">{m.kind === "pain" ? "Recaptured lost revenue" : "New income stream"}</div>
                    <div className="hsc-slide__income-label">{m.label}</div>
                    {m.competitor_cost && (
                      <div className="hsc-slide__income-row"><span>What others charge</span><strong>{m.competitor_cost}</strong></div>
                    )}
                    {m.uplift_range && (
                      <div className="hsc-slide__income-row"><span>Industry range</span><strong>${m.uplift_range[0].toLocaleString()}–${m.uplift_range[1].toLocaleString()}/mo</strong></div>
                    )}
                    {m.revenue_loss_mo > 0 && (
                      <div className="hsc-slide__income-row"><span>Currently losing</span><strong className="hsc-slide__income-red">${m.revenue_loss_mo.toLocaleString()}/mo</strong></div>
                    )}
                    <div className="hsc-slide__income-uplift">
                      <span>HustlePlate captures</span>
                      <strong>${Number(m.uplift_mo).toLocaleString()}<small>/mo</small></strong>
                    </div>
                    <p className="hsc-slide__income-evidence">{m.evidence}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="hsc-slide__empty">No qualitative streams flagged — toggle a few "yes" answers on the previous step to see this slide light up.</p>
            )}
            <div className="hsc-slide__bignum hsc-slide__bignum--amber" data-testid="hsc-slide-income-total">
              <div className="hsc-slide__bignum-eyebrow">B · Added Income</div>
              <div className="hsc-slide__bignum-num">${addedIncomeMo.toLocaleString()}<small>/mo</small></div>
              <div className="hsc-slide__bignum-sub">≈ <strong>${addedIncomeYr.toLocaleString()}</strong> per year</div>
            </div>
          </div>
        )}

        {/* ── Slide 6: A + B Finale ── */}
        {slide === 5 && (
          <div className="hsc-slide__body hsc-slide__body--finale">
            <div className="hsc-slide__head">
              <div className="hsc-slide__eyebrow hsc-slide__eyebrow--finale">Slide 6 · the finale</div>
              <h3 className="hsc-slide__h">A + B = Your total monthly revenue gain</h3>
            </div>
            <div className="hsc-slide__finale" data-testid="hsc-slide-finale">
              <div className="hsc-slide__finale-stack">
                <div className="hsc-slide__finale-card hsc-slide__finale-card--a">
                  <div className="hsc-slide__finale-card-mark"><PiggyBank /></div>
                  <div className="hsc-slide__finale-card-eyebrow">A · Savings</div>
                  <div className="hsc-slide__finale-card-num">${savingsMo.toLocaleString()}<small>/mo</small></div>
                  <div className="hsc-slide__finale-card-yr">${savingsYr.toLocaleString()}/yr</div>
                </div>
                <div className="hsc-slide__finale-op">+</div>
                <div className="hsc-slide__finale-card hsc-slide__finale-card--b">
                  <div className="hsc-slide__finale-card-mark"><Coins /></div>
                  <div className="hsc-slide__finale-card-eyebrow">B · Added income</div>
                  <div className="hsc-slide__finale-card-num">${addedIncomeMo.toLocaleString()}<small>/mo</small></div>
                  <div className="hsc-slide__finale-card-yr">${addedIncomeYr.toLocaleString()}/yr</div>
                </div>
                <div className="hsc-slide__finale-op">=</div>
                <div className="hsc-slide__finale-card hsc-slide__finale-card--total">
                  <div className="hsc-slide__finale-card-mark"><Trophy /></div>
                  <div className="hsc-slide__finale-card-eyebrow">Total revenue gain</div>
                  <div className="hsc-slide__finale-card-num">${totalGainMo.toLocaleString()}<small>/mo</small></div>
                  <div className="hsc-slide__finale-card-yr">${totalGainYr.toLocaleString()}/yr</div>
                </div>
              </div>
              <p className="hsc-slide__finale-line">
                That's <strong>${totalGainYr.toLocaleString()}</strong> extra in <strong>{form.business_name}</strong>'s pocket over the next 12 months
                — from one platform, one bill, one team to call.
              </p>
            </div>
          </div>
        )}

        {/* ── Slide 7: What's next ── */}
        {slide === 6 && (
          <div className="hsc-slide__body hsc-slide__body--next">
            <div className="hsc-slide__head">
              <div className="hsc-slide__eyebrow">Slide 7 · next steps</div>
              <h3 className="hsc-slide__h">Three ways to take this further</h3>
            </div>
            <div className="hsc-slide__next-grid">
              <div className="hsc-slide__next-card" data-testid="hsc-slide-next-email">
                <Mail className="hsc-slide__next-icon" />
                <div className="hsc-slide__next-h">Your report has been emailed</div>
                <p className="hsc-slide__next-p">
                  {result.email_sent
                    ? `A branded HTML copy has been sent to ${result.email_to}.`
                    : `We'll send your full report to ${result.email_to} shortly.`}
                </p>
              </div>
              <div className="hsc-slide__next-card" data-testid="hsc-slide-next-claim">
                <Sparkles className="hsc-slide__next-icon" />
                <div className="hsc-slide__next-h">Claim your business</div>
                <p className="hsc-slide__next-p">
                  Fast-track to a pre-filled "Watch It Live" onboard. We'll spin up your site in under 10 minutes.
                </p>
                <Link to="/add-business" className="sp-btn sp-btn--primary hsc-slide__next-btn" data-testid="hsc-slide-next-claim-cta">
                  Get started →
                </Link>
              </div>
              <div className="hsc-slide__next-card" data-testid="hsc-slide-next-call">
                <TrendingUp className="hsc-slide__next-icon" />
                <div className="hsc-slide__next-h">Talk to a specialist</div>
                <p className="hsc-slide__next-p">
                  A 1-business-day callback to walk through these slides, sanity-check the numbers, and answer questions.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Deck navigation (prev / dots / next) ──────────────────── */}
      <div className="hsc-deck__nav">
        <button
          type="button"
          onClick={goPrev}
          disabled={slide === 0}
          className="hsc-deck__nav-btn"
          data-testid="hsc-deck-prev"
        >
          <ChevronLeft /> Prev
        </button>
        <div className="hsc-deck__dots" role="tablist">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSlide(i)}
              className={`hsc-deck__dot ${i === slide ? "is-active" : ""}`}
              data-testid={`hsc-deck-dot-${s.id}`}
              aria-label={s.title}
              role="tab"
              aria-selected={i === slide}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={goNext}
          disabled={slide === total - 1}
          className="hsc-deck__nav-btn hsc-deck__nav-btn--primary"
          data-testid="hsc-deck-next"
        >
          Next <ChevronRight />
        </button>
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", testid }) {
  return (
    <div className="hsc-field">
      <label className="hsc-field__label">{label}</label>
      <input
        className="hsc-field__input"
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

/* Tri-state Yes/No pill row for qualitative questions */
function YnRow({ label, v, on, testid }) {
  return (
    <div className="hsc-yn-row" data-testid={testid}>
      <div className="hsc-yn-row__label">{label}</div>
      <div className="hsc-yn-row__btns">
        <button
          type="button"
          className={`hsc-yn-btn ${v === true ? "hsc-yn-btn--yes" : ""}`}
          onClick={() => on(true)}
          data-testid={`${testid}-yes`}
        >Yes</button>
        <button
          type="button"
          className={`hsc-yn-btn ${v === false ? "hsc-yn-btn--no" : ""}`}
          onClick={() => on(false)}
          data-testid={`${testid}-no`}
        >No</button>
      </div>
    </div>
  );
}

/* ── Onboarding 4-step ────────────────────────────────────────────────── */
function OnboardingSteps() {
  return (
    <section className="sp-section sp-section--cream" data-testid="sales-steps">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">How it works</div>
            <h2 className="sp-h">Cold-start to live website in four steps.<br />Roughly one cuppa.</h2>
          </div>
        </div>
        <div className="sp-steps__grid">
          {STEPS.map((s) => (
            <article key={s.n} className="sp-step" data-testid={`sales-step-${s.n}`}>
              <div className="sp-step__n">{s.n}</div>
              <div className="sp-step__h">{s.h}</div>
              <div className="sp-step__p">{s.p}</div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── FAQ ──────────────────────────────────────────────────────────────── */
function FaqSection() {
  return (
    <section id="faq" className="sp-section sp-section--paper" data-testid="sales-faq">
      <div className="sp-container">
        <div className="sp-head-row">
          <div>
            <div className="sp-eyebrow">Questions we get asked a lot</div>
            <h2 className="sp-h">Straight answers, no waffle.</h2>
          </div>
        </div>
        <div className="sp-faq__list">
          {FAQS.map((f, i) => (
            <details key={i} className="sp-faq__item" data-testid={`sales-faq-${i}`}>
              <summary>{f.q}</summary>
              <div className="sp-faq__body">{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── HustleCRM order form / Big CTA ─────────────────────────────────────────── */
function StartCta() {
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "" });
  const [status, setStatus] = useState("idle");
  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("sending");
    try {
      // Posts to our backend; the backend will forward to GHL_WEBHOOK_URL.
      await api.post("/sales/lead", { ...form, source: "platform_sales_page" });
      setStatus("sent");
    } catch (err) {
      setStatus("error");
    }
  }
  return (
    <section id="start" className="sp-section sp-section--paper" data-testid="sales-start">
      <div className="sp-container">
        <div className="sp-bigcta">
          <h2>Ready to keep your 30%?</h2>
          <p>
            Free Starter plan, set up in an hour, no credit card. We'll email
            you a login + a friendly Aussie human's mobile in case you get
            stuck.
          </p>
          {status !== "sent" && (
            <form onSubmit={handleSubmit} data-testid="sales-start-form" className="sp-bigcta__form">
              <input
                required placeholder="Your name" autoComplete="name"
                value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                data-testid="sales-start-name"
                className="sp-bigcta__input"
              />
              <input
                required placeholder="Restaurant / business name" autoComplete="organization"
                value={form.business} onChange={(e) => setForm({...form, business: e.target.value})}
                data-testid="sales-start-business"
                className="sp-bigcta__input"
              />
              <input
                required type="email" placeholder="Email" autoComplete="email"
                value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                data-testid="sales-start-email"
                className="sp-bigcta__input"
              />
              <input
                placeholder="Mobile (optional)" autoComplete="tel"
                value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                data-testid="sales-start-phone"
                className="sp-bigcta__input"
              />
              <button type="submit" className="sp-btn sp-btn--primary sp-bigcta__submit" disabled={status === "sending"} data-testid="sales-start-submit">
                {status === "sending" ? "Sending…" : "Get started free"}
              </button>
              {status === "error" && <p className="sp-bigcta__err">Couldn't reach the server. Try again or email us at hello@hustleplate.au.</p>}
            </form>
          )}
          {status === "sent" && (
            <div data-testid="sales-start-sent" className="sp-bigcta__sent">
              <strong>Thanks!</strong> Check your inbox in 30 seconds — we've
              sent your login link + a calendar invite for a no-obligation walk-through.
            </div>
          )}
          <div className="sp-bigcta__ctas">
            <a href={CALENDAR_URL} target="_blank" rel="noreferrer" className="sp-btn sp-btn--ghost" data-testid="sales-book-call">
              Or book a 15-min walk-through
            </a>
            <a href={DEMO_URL} target="_blank" rel="noreferrer" className="sp-btn sp-btn--ghost" data-testid="sales-live-demo">
              Open a live demo
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Recipe Library teaser — links to /recipes portal ────────────────── */
function RecipeLibraryTeaser() {
  // Pull a fresh batch of recipes (with images) from the live library so
  // the teaser feels alive and the dish names rotate every page-load.
  // Falls back to a hardcoded set if the API call fails so the section
  // never renders blank.
  const [picks, setPicks] = useState(null);
  const [count, setCount] = useState(2400);

  useEffect(() => {
    let cancelled = false;
    api.get("/hustle/recipes/index.json")
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        setCount(data.count || 2400);
        const withImages = (data.items || []).filter(
          (it) => it.image_url && !/placeholder/i.test(it.image_url),
        );
        // Pick 4 visually distinct hero cuisines if possible.
        const seen = new Set();
        const picked = [];
        for (const it of withImages) {
          if (picked.length >= 4) break;
          const cu = (it.cuisine || "").toLowerCase();
          if (seen.has(cu)) continue;
          seen.add(cu);
          picked.push(it);
        }
        // Top up if we couldn't fill 4 distinct cuisines.
        for (const it of withImages) {
          if (picked.length >= 4) break;
          if (!picked.find((p) => p.slug === it.slug)) picked.push(it);
        }
        setPicks(picked.length ? picked : null);
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  const showPicks = picks && picks.length > 0;
  const FALLBACK = [
    { c: "italian",  t: "Spaghetti alla Carbonara",  d: "20 min · Main" },
    { c: "japanese", t: "Miso-glazed Salmon",        d: "25 min · Main" },
    { c: "thai",     t: "Green Curry with Prawns",   d: "30 min · Main" },
    { c: "french",   t: "Crème Brûlée",              d: "1 hr · Dessert" },
  ];

  return (
    <section
      id="recipes"
      className="sp-recipes"
      data-testid="sales-recipes-teaser"
    >
      <div className="sp-container">
        <div className="sp-recipes__inner">
          <div className="sp-recipes__copy">
            <div className="sp-eyebrow sp-eyebrow--gold">Bonus library · free for every member</div>
            <h2 className="sp-h2">
              {count.toLocaleString()} recipes,<br/>twelve cuisines,<br/>one library.
            </h2>
            <p className="sp-lead">
              Every restaurant on HustlePlate gets backlinked from a public
              recipe portal that ranks for &ldquo;how to make X&rdquo; queries — and home cooks
              who land on it discover *your* restaurant nearby.
            </p>
            <p className="sp-lead">
              Italian. French. Chinese. Japanese. Indian. Thai. Vietnamese.
              Spanish. Lebanese. Mexican. Greek. Modern Australian.
            </p>
            <div className="sp-recipes__ctas">
              <a
                href="/recipes?sales=1"
                className="sp-btn sp-btn--primary"
                data-testid="sales-recipes-cta"
              >
                Browse the library →
              </a>
              <a
                href="https://hustlerecipes.au"
                target="_blank"
                rel="noreferrer"
                className="sp-btn sp-btn--ghost"
                data-testid="sales-recipes-member"
              >
                Become a HustleRecipes member
              </a>
            </div>
          </div>
          <div className="sp-recipes__cards" data-testid="sales-recipes-cards">
            {showPicks
              ? picks.map((r) => {
                  const minutes = (Number(r.cook_minutes) || 0) + (Number(r.prep_minutes) || 0);
                  const courseLabel = (r.course || "main").replace(/^./, (s) => s.toUpperCase());
                  return (
                    <a
                      key={r.slug}
                      href={`/recipes/${r.slug}?sales=1`}
                      className={`sp-recipe-card sp-recipe-card--${(r.cuisine || "italian").toLowerCase()} sp-recipe-card--live`}
                      data-testid={`sales-recipe-${r.slug}`}
                      style={{
                        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.7) 100%), url("${r.image_url}")`,
                      }}
                    >
                      <div className="sp-recipe-card__pill">{r.cuisine}</div>
                      <div className="sp-recipe-card__title">{r.title}</div>
                      <div className="sp-recipe-card__meta">{minutes ? `${minutes} min · ` : ""}{courseLabel}</div>
                    </a>
                  );
                })
              : FALLBACK.map((r) => (
                  <div key={r.c} className={`sp-recipe-card sp-recipe-card--${r.c}`}>
                    <div className="sp-recipe-card__pill">{r.c}</div>
                    <div className="sp-recipe-card__title">{r.t}</div>
                    <div className="sp-recipe-card__meta">{r.d}</div>
                  </div>
                ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Footer ───────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="sp-foot" data-testid="sales-footer">
      <div className="sp-container sp-foot__inner">
        <div className="sp-foot__brand">
          <HustlePlateLogo size={36} />
          <div className="sp-foot__sub">All-in-one restaurant platform · Made in Australia 🦘</div>
        </div>
        <div className="sp-foot__links">
          <a href="/recipes?sales=1" data-testid="sales-footer-recipes">Recipes</a>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="mailto:hello@hustleplate.au">hello@hustleplate.au</a>
        </div>
        <div className="sp-foot__cr">© {new Date().getFullYear()} HustlePlate Pty Ltd. All rights reserved.</div>
      </div>
    </footer>
  );
}

/* ── Page shell ───────────────────────────────────────────────────────── */
export default function SalesPage() {
  const [packs, setPacks] = useState([]);
  const [lang, setLang] = useState("en");
  // Translated content overlay (null = use English source verbatim)
  const [tx, setTx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sales-packs/public");
        setPacks(data.packs || []);
      } catch (e) {
        // Render with empty packs if the API hiccups — page still works.
      }
    })();
  }, []);

  useEffect(() => { document.title = "HustlePlate — One platform for your restaurant"; }, []);

  // Reflect lang in <html lang> for screen readers + Google's language signal.
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  // AEO/SEO JSON-LD for the agency marketing page itself (Organization +
  // WebSite + SoftwareApplication). Cleared on unmount so tenant routes
  // don't inherit agency-level schema.
  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "HustlePlate",
        url: origin,
        logo: `${origin}/favicon.ico`,
        description: "All-in-one restaurant platform: menu, bookings, online ordering, delivery, POS, and marketing — built in Australia.",
        areaServed: "AU",
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "HustlePlate",
        url: origin,
        potentialAction: {
          "@type": "SearchAction",
          target: `${origin}/?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "HustlePlate",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android",
        offers: { "@type": "Offer", priceCurrency: "AUD", price: "0" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "240" },
      },
    ];
    const nodes = schemas.map((s) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-seo-head", "platform");
      el.text = JSON.stringify(s);
      document.head.appendChild(el);
      return el;
    });
    // Hide any layout-scoped JSON-LD while on /platform.
    const layoutNodes = Array.from(document.querySelectorAll('script[data-seo-head="layout"]'));
    layoutNodes.forEach((n) => n.setAttribute("data-seo-suppressed", "1"));
    return () => {
      nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
      layoutNodes.forEach((n) => n.removeAttribute("data-seo-suppressed"));
    };
  }, []);

  return (
    <div className="sp-root" data-testid="sales-page">
      <SalesNav />
      <Hero tx={tx?.hero} />
      <PaymentStrip />
      <PainPoints />
      <SolutionSplit />
      <FeatureGrid />
      <SalesTemplateGallery
        heading={tx?.templates?.heading}
        subheading={tx?.templates?.subheading}
      />
      <CompareTable />
      <CuisineShowcase />
      <SalesItalianMenuDemo />
      <LoyaltyVouchersBlock />
      <CrossRedeemBlock />
      <CommunityBlock />
      <RecipeLibraryTeaser />
      <ReviewsBlock />
      <Pricing packs={packs} />
      <Testimonials />
      <RoiCalculator />
      <HustleSavingsCalculator />
      <OnboardingSteps />
      <FaqSection />
      <StartCta />
      <Footer />
    </div>
  );
}
